/**
 * DANAmark — Admin → Events → KB Hallen CMS.
 *
 * Reuses MembershipAdmin patterns (state header, section drawer, image
 * picker, publish batch). Adds sub-tabs for Programme, Artists,
 * Collaborators and Settings — all backed by dedicated endpoints in
 * server.py.
 */
import React, { useEffect, useState } from "react";
import { CSS } from "@dnd-kit/utilities";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import resolveImageUrl from "@/lib/imageUrl";
import ImagePicker from "./ImagePicker";

const META = {
  hero: "01 · Hero", why_venue: "02 · Why KB Hallen", the_idea: "03 · The Idea",
  what_to_expect: "04 · What to Expect", programme: "05 · Programme",
  artists: "06 · Artists & Participants", family_sport: "07 · Family & Sport",
  food: "08 · Food & Gastronomy", why_doing_it: "09 · Why We're Doing It",
  denmark_spain: "10 · Denmark + Spain", partners: "11 · Partners",
  tickets: "12 · Tickets", raffle_module: "13 · Raffle",
  social_run_proof: "14 · Social Run Proof", join_cta: "15 · Join",
  final_cta: "16 · Final CTA",
};
const LANGS = [{ code: "en", label: "English" }, { code: "da", label: "Dansk" }, { code: "es", label: "Español" }];
const TICKET_STATES = ["not_announced", "coming_soon", "on_sale", "sold_out", "free_entry"];
const PARTNER_CATEGORIES = ["institutional", "food", "sport", "cultural", "sponsor", "other"];

export default function KbHallenAdmin({ authed }) {
  const [tab, setTab] = useState("sections");
  const [settings, setSettings] = useState(null);
  const [sections, setSections] = useState(null);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = async () => {
    const [rSec, rSet] = await Promise.all([
      authed.get("/content?kind=kbh_section&status="),
      authed.get("/admin/events/kb-hallen-2026/settings").catch(() => ({ data: null })),
    ]);
    const rows = (rSec.data || []).slice().sort((a, b) => ((a.data?.order || 0) - (b.data?.order || 0)));
    setSections(rows);
    setSettings(rSet.data);
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line

  const seedIfNeeded = async () => {
    const r = await authed.post("/admin/events/kb-hallen-2026/seed-missing");
    setMsg(`Seeded ${r.data.sections_inserted} sections${r.data.event_created ? " + event record" : ""}.`);
    await load();
  };

  const setState = async (nextState) => {
    if ((settings?.page_state || "draft") === nextState) return;
    const prompts = {
      draft: "Take /events/kb-hallen-2026 OFFLINE? Public 404 restored.",
      ready: "Mark as READY? Public still 404; admin preview shareable.",
      live: "Publish /events/kb-hallen-2026 LIVE to the public?",
    };
    if (!window.confirm(prompts[nextState])) return;
    const r = await authed.put("/admin/events/kb-hallen-2026/settings", { page_state: nextState });
    setSettings(r.data);
    setMsg(`Page ${nextState.toUpperCase()}.`);
    setTimeout(() => setMsg(""), 3500);
  };

  if (sections === null) return <div className="text-ink/50 text-sm">Loading…</div>;
  if (!settings) return (
    <div className="border border-line rounded-xl p-8 bg-white text-center">
      <div className="text-ink/70 mb-4">KB Hallen event not seeded yet.</div>
      <button onClick={seedIfNeeded} className="px-4 py-2 bg-terracotta text-white text-[11px] uppercase tracking-widest">Seed KB Hallen defaults</button>
    </div>
  );

  const state = settings.page_state || "draft";
  const stateColor = { draft: "bg-line/30 text-ink/70", ready: "bg-amber-100 text-amber-800", live: "bg-emerald-100 text-emerald-800" }[state];

  return (
    <div className="space-y-6" data-testid="kbh-admin">
      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="label-mono text-red-700 text-[10px]">EVENTS · KB HALLEN (RETIRED)</div>
            <h2 className="font-editorial text-2xl md:text-3xl tracking-tighter mt-1">DANAmark at KB Hallen · 25 Oct 2026</h2>
            <div className="mt-2 text-xs text-ink/60 max-w-xl">
              This event is permanently cancelled. The public URL returns HTTP 410.
              Historical content is preserved here for audit only.
            </div>
            <div className="mt-3 flex items-center gap-3">
              <span data-testid="kbh-state-badge" className={`text-[10px] label-mono tracking-widest px-2 py-1 rounded ${stateColor}`}>{state.toUpperCase()}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-2">
              <button onClick={() => setState("draft")} disabled={state === "draft"} data-testid="kbh-state-draft"
                      className={`px-3 py-2 text-[11px] uppercase tracking-widest border ${state === "draft" ? "border-line text-ink/40 bg-line/10 cursor-default" : "border-ink text-ink hover:bg-ink hover:text-white"}`}>
                {state === "live" ? "Take offline" : "Draft"}
              </button>
              <button onClick={() => setState("ready")} disabled={state === "ready"}
                      className={`px-3 py-2 text-[11px] uppercase tracking-widest ${state === "ready" ? "bg-amber-100 text-amber-800 cursor-default" : "bg-amber-600 text-white hover:bg-amber-700"}`}>
                Mark ready
              </button>
              <button onClick={() => setState("live")} disabled={state === "live"} data-testid="kbh-state-live"
                      className={`px-3 py-2 text-[11px] uppercase tracking-widest ${state === "live" ? "bg-emerald-600 text-white cursor-default" : "bg-terracotta text-white hover:bg-terracotta-dark"}`}>
                Set page live
              </button>
            </div>
            {msg && <div className="text-[11px] text-ink/60">{msg}</div>}
          </div>
        </div>
      </section>

      <div className="flex flex-wrap gap-1 border-b border-line">
        {[["sections", "Sections"], ["programme", "Programme"], ["artists", "Artists"], ["collaborators", "Collaborators"], ["tickets", "Tickets"], ["settings", "Settings"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`kbh-tab-${k}`}
                  className={`px-4 py-2 text-[11px] uppercase tracking-widest ${tab === k ? "border-b-2 border-terracotta text-ink" : "text-ink/50 hover:text-ink"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "sections" && <SectionsList sections={sections} setSections={setSections} authed={authed} onEdit={setEditing} onReload={load} sensors={sensors} />}
      {tab === "programme" && <ProgrammeTab authed={authed} />}
      {tab === "artists" && <ArtistsTab authed={authed} />}
      {tab === "collaborators" && <CollabTab authed={authed} sections={sections} onReload={load} />}
      {tab === "tickets" && <TicketsTab authed={authed} settings={settings} onSave={s => setSettings(s)} />}
      {tab === "settings" && <SettingsTab authed={authed} settings={settings} onSave={s => setSettings(s)} />}

      {editing && <SectionEditor s={editing} authed={authed} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

// ─── Sections list ─────────────────────────────────────────────────────
function SectionsList({ sections, setSections, authed, onEdit, sensors }) {
  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oi = sections.findIndex(s => s.id === active.id);
    const ni = sections.findIndex(s => s.id === over.id);
    const next = arrayMove(sections, oi, ni).map((s, i) => ({ ...s, data: { ...s.data, order: i + 1 } }));
    setSections(next);
    await Promise.all(next.map(s => authed.put(`/content/${s.id}`, { id: s.id, kind: s.kind, status: s.status, data: s.data })));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {sections.map((s, i) => <Row key={s.id} s={s} i={i} onEdit={() => onEdit(s)} />)}
        </div>
      </SortableContext>
    </DndContext>
  );
}
function Row({ s, i, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const d = s.data || {};
  const thumb = d.images?.[0]?.url;
  const hidden = s.status !== "published" || d.visible === false;
  return (
    <div ref={setNodeRef} style={style} data-testid={`kbh-row-${d.slug}`}
         className={`bg-white border ${hidden ? "border-dashed border-line/60" : "border-line"} rounded-lg flex items-center gap-3 p-3`}>
      <button {...attributes} {...listeners} className="cursor-grab text-ink/30 text-xl px-2">≡</button>
      <div className="font-editorial text-lg tabular-nums w-8 text-ink/40">{i + 1}</div>
      <div className="w-14 h-14 shrink-0 border border-line/50 bg-line/10 overflow-hidden">
        {thumb ? <img src={resolveImageUrl(thumb)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-ink/25 text-[10px]">—</div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-editorial text-base truncate">{META[d.slug] || d.slug}</div>
        <div className="text-[11px] text-ink/50 truncate">{d.template} · {(d.headline?.en || "").slice(0, 60)}</div>
      </div>
      <span className={`text-[10px] label-mono tracking-widest px-2 py-1 rounded ${hidden ? "bg-line/30 text-ink/60" : "bg-emerald-100 text-emerald-800"}`}>
        {hidden ? "HIDDEN" : "PUBLISHED"}
      </span>
      <button onClick={onEdit} data-testid={`kbh-edit-${d.slug}`}
              className="border border-ink text-ink px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-ink hover:text-white">Edit</button>
    </div>
  );
}

// ─── Section editor drawer ─────────────────────────────────────────────
function SectionEditor({ s, authed, onClose }) {
  const [data, setData] = useState(s.data_draft || s.data || {});
  const [lang, setLang] = useState("en");
  const [showPicker, setShowPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const patch = (k, v) => setData(p => ({ ...p, [k]: v }));
  const patchLang = (k, v) => setData(p => ({ ...p, [k]: { ...(p[k] || {}), [lang]: v } }));
  const patchImage = (idx, po) => {
    const imgs = [...(data.images || [])];
    imgs[idx] = { ...(imgs[idx] || {}), ...po };
    patch("images", imgs);
  };
  const saveDraft = async () => { setBusy(true); await authed.post(`/content/${s.id}/draft`, { data }); setBusy(false); };
  const publish = async () => { setBusy(true); await authed.post(`/content/${s.id}/publish`, { data }); setBusy(false); onClose(); };
  const t = data.template;
  const needsImage = ["hero", "stats_showcase", "final_cta"].includes(t);
  const primary = (data.images || [])[0];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" role="dialog">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-line px-6 py-4 flex items-center justify-between">
          <div>
            <div className="label-mono text-terracotta text-[10px]">EDIT SECTION</div>
            <div className="font-editorial text-xl">{META[data.slug] || data.slug}</div>
          </div>
          <button onClick={onClose} className="text-2xl text-ink/50 px-2">×</button>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex gap-2">
            {LANGS.map(l => <button key={l.code} onClick={() => setLang(l.code)}
                                    className={`px-3 py-1 text-[11px] uppercase tracking-widest ${lang === l.code ? "bg-ink text-white" : "border border-line text-ink/60"}`}>{l.label}</button>)}
          </div>
          <F label={`Eyebrow (${lang})`}><input value={(data.eyebrow || {})[lang] || ""} onChange={e => patchLang("eyebrow", e.target.value)} className="border border-line px-3 py-2 w-full label-mono tracking-widest text-sm" /></F>
          <F label={`Headline (${lang})`}><textarea rows={2} value={(data.headline || {})[lang] || ""} onChange={e => patchLang("headline", e.target.value)} className="border border-line px-3 py-2 w-full font-editorial text-2xl leading-tight" /></F>
          {t !== "categories" && (
            <F label={`Body (${lang})`}><textarea rows={6} value={(data.body || {})[lang] || ""} onChange={e => patchLang("body", e.target.value)} className="border border-line px-3 py-2 w-full text-sm leading-relaxed" /></F>
          )}
          {["hero", "editorial_text", "stats_showcase", "join", "final_cta", "why_doing_it"].includes(t) || t === "editorial_text" ? (
            <>
              <F label={`CTA label (${lang})`}><input value={(data.cta_label || {})[lang] || ""} onChange={e => patchLang("cta_label", e.target.value)} className="border border-line px-3 py-2 w-full" /></F>
              <F label="CTA URL or scroll target"><input value={data.cta_url || data.cta_scroll_to || ""} onChange={e => patch(e.target.value.startsWith("/") ? "cta_url" : "cta_scroll_to", e.target.value)} className="border border-line px-3 py-2 w-full text-sm" placeholder="/join or programme" /></F>
            </>
          ) : null}
          {["hero", "join", "final_cta"].includes(t) && (
            <>
              <F label={`Secondary CTA label (${lang})`}><input value={(data.cta2_label || {})[lang] || ""} onChange={e => patchLang("cta2_label", e.target.value)} className="border border-line px-3 py-2 w-full" /></F>
              <F label="Secondary CTA URL"><input value={data.cta2_url || ""} onChange={e => patch("cta2_url", e.target.value)} className="border border-line px-3 py-2 w-full text-sm" /></F>
            </>
          )}
          {t === "categories" && <CategoriesEditor data={data} lang={lang} patch={patch} />}
          {t === "raffle" && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!data.show_raffle} onChange={e => patch("show_raffle", e.target.checked)} className="w-4 h-4 accent-terracotta" />
              Show raffle module on this event page
            </label>
          )}
          {needsImage && (
            <div className="border-t border-line pt-5">
              <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-2">IMAGE</div>
              {primary?.url ? (
                <div className="space-y-3">
                  <div className="aspect-[16/9] bg-line/10 overflow-hidden">
                    <img src={resolveImageUrl(primary.url)} alt="" className="w-full h-full object-cover"
                         style={{ objectPosition: `${primary.focal?.x ?? 50}% ${primary.focal?.y ?? 50}%` }} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FocalGrid label="Desktop focal" value={primary.focal} onChange={f => patchImage(0, { focal: f })} />
                    <FocalGrid label="Mobile focal" value={primary.focal_mobile} onChange={f => patchImage(0, { focal_mobile: f })} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowPicker(true)} className="border border-ink text-ink px-3 py-1.5 text-[11px] uppercase tracking-widest">Replace</button>
                    <button onClick={() => patchImage(0, { url: "" })} className="border border-line text-ink/60 px-3 py-1.5 text-[11px] uppercase tracking-widest">Remove</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowPicker(true)} className="border-2 border-dashed border-line px-6 py-8 w-full text-ink/50 hover:border-ink hover:text-ink">+ Select from Media Library</button>
              )}
            </div>
          )}
          <div className="border-t border-line pt-5 flex items-center justify-between">
            <div className="text-sm">{data.visible === false ? "Hidden from public" : "Visible when page is live"}</div>
            <button onClick={() => patch("visible", data.visible === false)} className={`px-3 py-2 text-[11px] uppercase tracking-widest ${data.visible === false ? "bg-ink text-white" : "border border-ink text-ink"}`}>
              {data.visible === false ? "Show" : "Hide"}
            </button>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-line px-6 py-4 flex justify-end gap-2">
          <button onClick={saveDraft} disabled={busy} className="px-4 py-2 text-[11px] uppercase tracking-widest border border-ink text-ink hover:bg-ink hover:text-white">Save draft</button>
          <button onClick={publish} disabled={busy} className="px-4 py-2 text-[11px] uppercase tracking-widest bg-terracotta text-white hover:bg-terracotta-dark">{busy ? "…" : "Publish"}</button>
        </div>
      </div>
      {showPicker && <ImagePicker onPick={(a) => { patchImage(0, { url: a.url, alt: primary?.alt || { en: a.alt_text || "", da: a.alt_text || "", es: a.alt_text || "" } }); setShowPicker(false); }} onClose={() => setShowPicker(false)} authed={authed} />}
    </div>
  );
}

function CategoriesEditor({ data, lang, patch }) {
  const cats = data.categories || [];
  const setCat = (i, k, v) => {
    const next = [...cats];
    next[i] = { ...next[i], [k]: k === "title" || k === "text" ? { ...(next[i][k] || {}), [lang]: v } : v };
    patch("categories", next);
  };
  return (
    <F label={`Categories (${lang})`}>
      <div className="space-y-3">
        {cats.map((c, i) => (
          <div key={c.key || i} className="border border-line rounded p-3 space-y-2">
            <input value={c.title?.[lang] || ""} onChange={e => setCat(i, "title", e.target.value)}
                   className="border border-line px-3 py-2 w-full label-mono tracking-widest text-sm" placeholder="Title" />
            <textarea value={c.text?.[lang] || ""} onChange={e => setCat(i, "text", e.target.value)}
                      rows={2} className="border border-line px-3 py-2 w-full text-sm" placeholder="Description" />
          </div>
        ))}
      </div>
    </F>
  );
}

function F({ label, children }) {
  return <label className="block"><div className="label-mono text-[11px] tracking-widest text-ink/60 mb-1">{label}</div>{children}</label>;
}

function FocalGrid({ label, value, onChange }) {
  const pts = [{ x: 0, y: 0 }, { x: 50, y: 0 }, { x: 100, y: 0 }, { x: 0, y: 50 }, { x: 50, y: 50 }, { x: 100, y: 50 }, { x: 0, y: 100 }, { x: 50, y: 100 }, { x: 100, y: 100 }];
  const cur = value || { x: 50, y: 50 };
  return (
    <div>
      <div className="label-mono text-[10px] tracking-widest text-ink/60 mb-1">{label}</div>
      <div className="grid grid-cols-3 gap-1 w-fit border border-line p-1">
        {pts.map(p => <button key={`${p.x}-${p.y}`} onClick={() => onChange(p)} className={`w-6 h-6 rounded-full ${cur.x === p.x && cur.y === p.y ? "bg-terracotta" : "bg-line/30 hover:bg-ink/40"}`} />)}
      </div>
    </div>
  );
}

// ─── Programme tab ─────────────────────────────────────────────────────
function ProgrammeTab({ authed }) {
  const [items, setItems] = useState([]);
  const [artists, setArtists] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => {
    const [rP, rA] = await Promise.all([
      authed.get("/admin/events/kb-hallen-2026/programme"),
      authed.get("/admin/artists"),
    ]);
    setItems(rP.data); setArtists(rA.data);
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line
  const del = async (id) => { if (!window.confirm("Delete this programme item?")) return; await authed.delete(`/admin/events/kb-hallen-2026/programme/${id}`); load(); };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-ink/60">{items.length} programme item(s)</div>
        <button onClick={() => setEditing({})} data-testid="kbh-programme-add" className="px-4 py-2 text-[11px] uppercase tracking-widest bg-ink text-white">+ Add item</button>
      </div>
      <div className="space-y-2">
        {items.map(it => {
          const d = it.data || {};
          const artist = artists.find(a => a.id === d.artist_id);
          return (
            <div key={it.id} className="bg-white border border-line p-3 flex items-center gap-3">
              <div className="font-editorial text-xl tabular-nums w-16 text-terracotta">{d.time || "—"}</div>
              <div className="flex-1 min-w-0">
                <div className="font-editorial truncate">{d.title?.en || "(untitled)"}</div>
                <div className="text-[11px] text-ink/50">{d.category} · {d.item_status}{artist ? ` · ${artist.data?.name}` : ""}</div>
              </div>
              <button onClick={() => setEditing(it)} className="border border-ink px-3 py-1 text-[10px] uppercase tracking-widest">Edit</button>
              <button onClick={() => del(it.id)} className="border border-rose-300 text-rose-600 px-3 py-1 text-[10px] uppercase tracking-widest">Delete</button>
            </div>
          );
        })}
      </div>
      {editing !== null && <ProgrammeEditor item={editing} artists={artists} authed={authed} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ProgrammeEditor({ item, artists, authed, onClose }) {
  const [f, setF] = useState(item.data || { title: {}, description: {}, item_status: "confirmed", event_slug: "kb-hallen-2026" });
  const [lang, setLang] = useState("en");
  const save = async () => {
    if (item.id) await authed.put(`/admin/events/kb-hallen-2026/programme/${item.id}`, f);
    else await authed.post("/admin/events/kb-hallen-2026/programme", f);
    onClose();
  };
  const patch = (k, v) => setF(p => ({ ...p, [k]: v }));
  const patchL = (k, v) => setF(p => ({ ...p, [k]: { ...(p[k] || {}), [lang]: v } }));
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white max-w-lg w-full rounded-xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="font-editorial text-xl">{item.id ? "Edit" : "New"} programme item</div>
        <div className="flex gap-2">{LANGS.map(l => <button key={l.code} onClick={() => setLang(l.code)} className={`px-2 py-1 text-[10px] uppercase ${lang === l.code ? "bg-ink text-white" : "border border-line"}`}>{l.label}</button>)}</div>
        <F label="Time (HH:MM)"><input value={f.time || ""} onChange={e => patch("time", e.target.value)} data-testid="prog-time" className="border border-line px-3 py-2 w-full" /></F>
        <F label={`Title (${lang})`}><input value={f.title?.[lang] || ""} onChange={e => patchL("title", e.target.value)} data-testid="prog-title" className="border border-line px-3 py-2 w-full font-editorial text-lg" /></F>
        <F label={`Description (${lang})`}><textarea rows={3} value={f.description?.[lang] || ""} onChange={e => patchL("description", e.target.value)} className="border border-line px-3 py-2 w-full text-sm" /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Category"><select value={f.category || ""} onChange={e => patch("category", e.target.value)} className="border border-line px-3 py-2 w-full bg-white"><option value="">—</option>{["music", "food", "sport", "family", "culture", "solidarity"].map(c => <option key={c}>{c}</option>)}</select></F>
          <F label="Status"><select value={f.item_status || "confirmed"} onChange={e => patch("item_status", e.target.value)} className="border border-line px-3 py-2 w-full bg-white">{["confirmed", "tba", "hidden"].map(c => <option key={c}>{c}</option>)}</select></F>
          <F label="Artist"><select value={f.artist_id || ""} onChange={e => patch("artist_id", e.target.value || null)} className="border border-line px-3 py-2 w-full bg-white"><option value="">—</option>{artists.map(a => <option key={a.id} value={a.id}>{a.data?.name}</option>)}</select></F>
          <F label="Location"><input value={f.location || ""} onChange={e => patch("location", e.target.value)} className="border border-line px-3 py-2 w-full" /></F>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <button onClick={onClose} className="px-4 py-2 text-[11px] uppercase tracking-widest border border-ink">Cancel</button>
          <button onClick={save} data-testid="prog-save" className="px-4 py-2 text-[11px] uppercase tracking-widest bg-terracotta text-white">Save</button>
        </div>
      </div>
    </div>
  );
}

// ─── Artists tab ───────────────────────────────────────────────────────
function ArtistsTab({ authed }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => { const r = await authed.get("/admin/artists"); setList(r.data); };
  useEffect(() => { load(); }, []);   // eslint-disable-line
  const del = async (id) => { if (!window.confirm("Delete artist?")) return; await authed.delete(`/admin/artists/${id}`); load(); };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-ink/60">{list.length} artist(s)</div>
        <button onClick={() => setEditing({})} data-testid="kbh-artist-add" className="px-4 py-2 text-[11px] uppercase tracking-widest bg-ink text-white">+ Add artist</button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {list.map(a => {
          const d = a.data || {};
          return (
            <div key={a.id} className="bg-white border border-line p-3 flex gap-3 items-center">
              <div className="w-14 h-14 shrink-0 bg-line/20 overflow-hidden">
                {d.photo ? <img src={resolveImageUrl(d.photo)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-ink/25 text-[10px]">—</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-editorial">{d.name}</div>
                <div className="text-[11px] text-ink/60 flex gap-2">
                  <span className={`px-1.5 py-0.5 rounded ${d.confirmed ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{d.confirmed ? "CONFIRMED" : "DRAFT"}</span>
                  {!d.active && <span className="text-rose-600">inactive</span>}
                </div>
              </div>
              <button onClick={() => setEditing(a)} className="border border-ink px-3 py-1 text-[10px] uppercase tracking-widest">Edit</button>
              <button onClick={() => del(a.id)} className="border border-rose-300 text-rose-600 px-3 py-1 text-[10px] uppercase tracking-widest">Del</button>
            </div>
          );
        })}
      </div>
      {editing !== null && <ArtistEditor item={editing} authed={authed} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ArtistEditor({ item, authed, onClose }) {
  const [f, setF] = useState(item.data || { name: "", role: {}, bio: {}, active: true, confirmed: false });
  const [lang, setLang] = useState("en");
  const [showPicker, setShowPicker] = useState(false);
  const patch = (k, v) => setF(p => ({ ...p, [k]: v }));
  const patchL = (k, v) => setF(p => ({ ...p, [k]: { ...(p[k] || {}), [lang]: v } }));
  const save = async () => {
    if (item.id) await authed.put(`/admin/artists/${item.id}`, f);
    else await authed.post("/admin/artists", f);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white max-w-lg w-full rounded-xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="font-editorial text-xl">{item.id ? "Edit" : "New"} artist</div>
        <F label="Name"><input value={f.name || ""} onChange={e => patch("name", e.target.value)} data-testid="artist-name" className="border border-line px-3 py-2 w-full font-editorial text-lg" /></F>
        <div className="flex gap-2">{LANGS.map(l => <button key={l.code} onClick={() => setLang(l.code)} className={`px-2 py-1 text-[10px] uppercase ${lang === l.code ? "bg-ink text-white" : "border border-line"}`}>{l.label}</button>)}</div>
        <F label={`Role (${lang})`}><input value={f.role?.[lang] || ""} onChange={e => patchL("role", e.target.value)} className="border border-line px-3 py-2 w-full" /></F>
        <F label={`Bio (${lang})`}><textarea rows={3} value={f.bio?.[lang] || ""} onChange={e => patchL("bio", e.target.value)} className="border border-line px-3 py-2 w-full text-sm" /></F>
        <F label="Photo">
          {f.photo ? <div className="space-y-2"><img src={resolveImageUrl(f.photo)} alt="" className="w-32 h-40 object-cover" /><div className="flex gap-2"><button onClick={() => setShowPicker(true)} className="border border-ink px-3 py-1 text-[10px] uppercase tracking-widest">Replace</button><button onClick={() => patch("photo", "")} className="border border-line text-ink/60 px-3 py-1 text-[10px] uppercase tracking-widest">Remove</button></div></div>
                   : <button onClick={() => setShowPicker(true)} className="border-2 border-dashed border-line px-4 py-6 w-full text-ink/50">+ Select from Media Library</button>}
        </F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Website"><input value={f.website || ""} onChange={e => patch("website", e.target.value)} className="border border-line px-3 py-2 w-full text-sm" /></F>
          <F label="Instagram"><input value={f.social?.instagram || ""} onChange={e => patch("social", { ...(f.social || {}), instagram: e.target.value })} className="border border-line px-3 py-2 w-full text-sm" /></F>
        </div>
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!f.confirmed} onChange={e => patch("confirmed", e.target.checked)} data-testid="artist-confirmed" className="accent-terracotta w-4 h-4" /> Confirmed</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.active !== false} onChange={e => patch("active", e.target.checked)} className="accent-terracotta w-4 h-4" /> Active</label>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <button onClick={onClose} className="px-4 py-2 text-[11px] uppercase tracking-widest border border-ink">Cancel</button>
          <button onClick={save} data-testid="artist-save" className="px-4 py-2 text-[11px] uppercase tracking-widest bg-terracotta text-white">Save</button>
        </div>
      </div>
      {showPicker && <ImagePicker onPick={(a) => { patch("photo", a.url); setShowPicker(false); }} onClose={() => setShowPicker(false)} authed={authed} />}
    </div>
  );
}

// ─── Collaborators tab ─────────────────────────────────────────────────
function CollabTab({ authed, sections, onReload }) {
  const [all, setAll] = useState([]);
  const partnersSec = sections.find(s => s.data?.template === "partners");
  const [refs, setRefs] = useState((partnersSec?.data?.collaborators) || []);
  const load = async () => { const r = await authed.get("/content?kind=collaborator&status=published"); setAll(r.data || []); };
  useEffect(() => { load(); }, []);   // eslint-disable-line
  const isSelected = (slug) => refs.some(r => r.slug === slug);
  const toggle = (slug) => setRefs(prev => isSelected(slug) ? prev.filter(r => r.slug !== slug) : [...prev, { slug, category: "other", order: prev.length }]);
  const setCategory = (slug, cat) => setRefs(prev => prev.map(r => r.slug === slug ? { ...r, category: cat } : r));
  const save = async () => {
    const data = { ...(partnersSec.data || {}), collaborators: refs };
    await authed.post(`/content/${partnersSec.id}/publish`, { data });
    await onReload();
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-ink/60">{refs.length} of {all.length} collaborators selected</div>
        <button onClick={save} data-testid="kbh-collab-save" className="px-4 py-2 text-[11px] uppercase tracking-widest bg-terracotta text-white">Save selection</button>
      </div>
      <div className="grid md:grid-cols-2 gap-2 max-h-[70vh] overflow-y-auto">
        {all.map(c => {
          const d = c.data || {};
          const sel = isSelected(d.slug);
          const cat = refs.find(r => r.slug === d.slug)?.category || "other";
          return (
            <div key={c.id} className={`bg-white border p-3 flex items-center gap-3 ${sel ? "border-terracotta" : "border-line"}`}>
              <input type="checkbox" checked={sel} onChange={() => toggle(d.slug)} className="w-4 h-4 accent-terracotta" />
              <div className="w-12 h-12 shrink-0 bg-line/10 flex items-center justify-center overflow-hidden">
                {d.logo ? <img src={resolveImageUrl(d.logo)} alt="" className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] text-ink/30">—</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{d.name}</div>
                {sel && (
                  <select value={cat} onChange={e => setCategory(d.slug, e.target.value)} className="mt-1 border border-line px-2 py-1 text-[10px] uppercase tracking-widest">
                    {PARTNER_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Tickets tab ───────────────────────────────────────────────────────
function TicketsTab({ authed, settings, onSave }) {
  const [t, setT] = useState(settings.tickets || {});
  const [saved, setSaved] = useState(false);
  const patch = (k, v) => setT(p => ({ ...p, [k]: v }));
  const save = async () => {
    const r = await authed.put("/admin/events/kb-hallen-2026/settings", { tickets: t });
    onSave(r.data); setSaved(true); setTimeout(() => setSaved(false), 2000);
  };
  return (
    <div className="bg-white border border-line rounded-xl p-6 space-y-4">
      <F label="Status">
        <select value={t.status || "not_announced"} onChange={e => patch("status", e.target.value)} data-testid="ticket-status" className="border border-line px-3 py-2 bg-white">
          {TICKET_STATES.map(s => <option key={s}>{s}</option>)}
        </select>
      </F>
      <div className="grid md:grid-cols-3 gap-4">
        <F label="Price"><input type="number" value={t.price || ""} onChange={e => patch("price", e.target.value ? Number(e.target.value) : null)} className="border border-line px-3 py-2 w-full" /></F>
        <F label="Currency"><input value={t.currency || "DKK"} onChange={e => patch("currency", e.target.value)} className="border border-line px-3 py-2 w-full" /></F>
        <F label="Capacity"><input type="number" value={t.capacity || ""} onChange={e => patch("capacity", e.target.value ? Number(e.target.value) : null)} className="border border-line px-3 py-2 w-full" /></F>
      </div>
      <F label="Ticket URL"><input value={t.url || ""} onChange={e => patch("url", e.target.value)} data-testid="ticket-url" className="border border-line px-3 py-2 w-full text-sm" placeholder="https://..." /></F>
      <div className="text-xs text-ink/50">CTA labels and notes are translated inline in each language; leave empty to auto-generate from status.</div>
      <button onClick={save} className="px-4 py-2 text-[11px] uppercase tracking-widest bg-terracotta text-white">{saved ? "Saved" : "Save tickets"}</button>
    </div>
  );
}

// ─── Settings tab ──────────────────────────────────────────────────────
function SettingsTab({ authed, settings, onSave }) {
  const [f, setF] = useState(settings);
  const [saved, setSaved] = useState(false);
  const patch = (k, v) => setF(p => ({ ...p, [k]: v }));
  const patchL = (k, l, v) => setF(p => ({ ...p, [k]: { ...(p[k] || {}), [l]: v } }));
  const patchSEO = (l, v) => setF(p => ({ ...p, seo: { ...(p.seo || {}), title: { ...(p.seo?.title || {}), [l]: v } } }));
  const patchSEODesc = (l, v) => setF(p => ({ ...p, seo: { ...(p.seo || {}), description: { ...(p.seo?.description || {}), [l]: v } } }));
  const save = async () => {
    const r = await authed.put("/admin/events/kb-hallen-2026/settings", {
      title: f.title, date_iso: f.date_iso, start_time: f.start_time, end_time: f.end_time,
      venue: f.venue, city: f.city, status: f.status, seo: f.seo,
    });
    onSave(r.data); setSaved(true); setTimeout(() => setSaved(false), 2000);
  };
  return (
    <div className="bg-white border border-line rounded-xl p-6 space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <F label="Slug"><input value={f.slug || "kb-hallen-2026"} disabled className="border border-line px-3 py-2 w-full bg-line/10 text-ink/50" /></F>
        <F label="Date"><input type="date" value={f.date_iso || ""} onChange={e => patch("date_iso", e.target.value)} className="border border-line px-3 py-2 w-full" /></F>
        <F label="Venue"><input value={f.venue || ""} onChange={e => patch("venue", e.target.value)} className="border border-line px-3 py-2 w-full" /></F>
        <F label="City"><input value={f.city || ""} onChange={e => patch("city", e.target.value)} className="border border-line px-3 py-2 w-full" /></F>
      </div>
      {LANGS.map(l => <F key={l.code} label={`Title (${l.label})`}><input value={f.title?.[l.code] || ""} onChange={e => patchL("title", l.code, e.target.value)} className="border border-line px-3 py-2 w-full font-editorial" /></F>)}
      <div className="pt-4 border-t border-line">
        <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-2">SEO</div>
        {LANGS.map(l => (
          <div key={l.code} className="mb-3">
            <F label={`SEO title (${l.label})`}><input value={f.seo?.title?.[l.code] || ""} onChange={e => patchSEO(l.code, e.target.value)} className="border border-line px-3 py-2 w-full text-sm" /></F>
            <F label={`Meta description (${l.label})`}><textarea rows={2} value={f.seo?.description?.[l.code] || ""} onChange={e => patchSEODesc(l.code, e.target.value)} className="border border-line px-3 py-2 w-full text-sm" /></F>
          </div>
        ))}
      </div>
      <button onClick={save} data-testid="kbh-settings-save" className="px-4 py-2 text-[11px] uppercase tracking-widest bg-terracotta text-white">{saved ? "Saved" : "Save settings"}</button>
    </div>
  );
}
